import $ from 'jquery';
import Immutable from 'immutable';
import BasePropertyView from 'BasePropertyView.js';
import _template from 'properties/table/column-main-number.ejs';
import util from 'util/Util.js';
import TextUtil from 'util/TextUtil.js';
import numericUtil from 'shared/NumericUtil';
import constants        from 'Constants.js';

// PF-5745
import undoService from 'UndoService';
import textConstants from 'TextConstants';

class MainNumericView extends BasePropertyView {

    initialize( opts ) {
        this.options = opts || {};
        this.template = _template;
        this.tagName = 'div';
        this.id = opts.el;
        this.colId = this.model.get('selectedRowId');
        this.selectedColor = 'lightgrey';
        this.unselectedColor = 'transparent';
        this.events = {
            'change input[type=number], input[type=text], select' : 'updateAttributes',
            'blur input[type=text], input[type=number]'   : 'lostFocus',
            'focus input[type=text], input[type=number]'   : 'getFocus',
            'click img' : 'switchAlignment',
            'change input[type=checkbox]' : 'updateCheckAttributes',
            'keydown input[type=text]' : 'validateInputKeyPressed'
        };

        if(this.colId){
            let factorykey=this.model.get('gridData')[this.colId].get('factoryKey');
            util.updateHelpDocumentationLink(factorykey);
        }

        if (!this.columnModel.get('align')) {
            this.columnModel.set('align', 'right', {silent:true});
        }

        this.clearAlignmentSelections();

        const alignmentHilight =
            { 'left': 'alignLeftSelection', 'center': 'alignCenterSelection', 'right': 'alignRightSelection'}[this.columnModel.get('align')];
        this.columnModel.set( alignmentHilight, this.selectedColor, {silent: true});


        this.listenTo(this.model, 'change', this.render);
        return this;
    }

    validatePositiveNumeric(ev){
        TextUtil.validatePositiveNumericNotPeriod(ev);
    }

    validateInputKeyPressed(ev) {
        const propName = ev.currentTarget.name;

        if (propName === 'text' || propName === 'min' || propName === 'max') { //Apply all validation rules to the input text for numeric value
            let allowNegative = util.isTrue(this.columnModel.get('allowNegative'));

            if (propName === 'min' || propName === 'max'){ //For min and max always accept negative
                allowNegative = true;
            }

            if ($.inArray(ev.keyCode, [8, 9, 13, 27, 46]) !== -1 ||
                // Allow: Ctrl+C, Command+C, Ctrl+V, Command+V, Ctrl+X, Command+X
                ((ev.keyCode === 67 || ev.keyCode === 86 || ev.keyCode === 88) && (ev.ctrlKey === true || ev.metaKey === true)) ||
                // Allow: home, end, left, right, down, up
                (ev.keyCode >= 35 && ev.keyCode <= 40)) {
                return;
            }

            if ($.inArray(ev.keyCode, [109, 189]) !== -1) {
                //If negative is allowed then avoid introducing more than one sign
                if (!allowNegative || ev.currentTarget.value.indexOf('-') !== -1) {
                    ev.preventDefault();
                }
            } else if ($.inArray(ev.keyCode, [110, 190]) !== -1) {
                //If decimal point is allowed then avoid introducing more than one point
                if (ev.currentTarget.value.indexOf('.') !== -1) {
                    ev.preventDefault();
                }
            } else if ((ev.shiftKey || (ev.keyCode < 48 || ev.keyCode > 57)) && (ev.keyCode < 96 || ev.keyCode > 105)) {
                // Ensure that it is a number and stop the keypress
                ev.preventDefault();
            }
        }
    }   

    get columnModel() {
        return this.model.get('gridData')[this.colId];
    }
    
    transformNumericValue() {
        //real number
        const number = this.model.get('gridData')[this.colId].attributes['text'];

        //buffer
        var numericValue = number;
        var temp;

        //properties
        const leadingZero = this.model.get('gridData')[this.colId].attributes['showLeadingZero'];
        const currencySymbol = this.model.get('gridData')[this.colId].attributes['symbol'];
        const decimalLength = this.model.get('gridData')[this.colId].attributes['decimalLength'];
        const thousandsSeparator = this.model.get('gridData')[this.colId].attributes['thousandsSeparator'];
        const decimalSeparator = this.model.get('gridData')[this.colId].attributes['decimalSeparator'];

        const addZero = this.model.get('gridData')[this.colId].attributes['addZero'];
        const paddingAmount = this.model.get('gridData')[this.colId].attributes['paddingAmount'];
        const paddingAlignment = this.model.get('gridData')[this.colId].attributes['paddingAlignment'];

        //initial number 0.00
        if(leadingZero && number === 0) {
            numericValue = number.toFixed(decimalLength);
        }
        if(decimalLength > 0) {
            numericValue = parseInt(number).toFixed(decimalLength);
            //change dot per comma since there is just two separators.
            if(decimalSeparator !== '.') {
                numericValue = numericValue.replace('.',decimalSeparator);
            }
        }
        if(number >= 1000) {
            numericValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, (thousandsSeparator==='None' ? '' : thousandsSeparator));
        }
        if(currencySymbol !== '') {
            numericValue = currencySymbol + numericValue;
        }

        if(addZero==='checked') {
            const zeros = "0".repeat(paddingAmount);
            if(paddingAlignment ==='Leading') {
                numericValue = zeros + numericValue;
            } else {
                numericValue = numericValue + zeros;
            }
        }

        this.model.get('gridData')[this.colId].attributes['text'] = numericValue;
        this.model.get('gridData')[this.colId].attributes['numericValue'] = numericValue;
    }

    updateAttributes(ev) {
        ev.preventDefault();
        const propName = ev.currentTarget.name;
        let propVal  = ev.currentTarget.value;
        let oldPropVal = this.columnModel.get(propName);
        let isValid = true;

        if (propName === 'decimalLength') {
            const maxDecimalLength = constants.TABLE_NUMERIC_INPUT_MAX_DECIMALS_LENGTH;
            if (isNaN(propVal) || util.extractNum(propVal) <= 0) {
                propVal = 0;
            } else if (util.extractNum(propVal) > maxDecimalLength) {
                propVal = maxDecimalLength;
            }
        } else if (propName === 'decimalSeparator') { //Manage rules between decimalSeparator and thousandsSeparator on designer
            if (this.columnModel.get('thousandsSeparator') === propVal) {
                isValid = false;

                let validVal = ',';
                if (propVal === ',') { //There are only two possible values for decimalSeparator
                    validVal = '.';
                }
                this.columnModel.set(propName, validVal, { silent: true });
            }
        } else if (propName === 'thousandsSeparator') {
            if (this.columnModel.get('decimalSeparator') === propVal) {
                isValid = false;
                this.columnModel.set(propName, '', { silent: true });
            }
        } else if (propName === 'min' || propName === 'max') {
            const minValueAccepted = constants.NUMERIC_INPUT_MIN_VALUE;
            const maxValueAccepted = constants.NUMERIC_INPUT_MAX_VALUE;
            const min = util.extractNum(this.columnModel.get('min'));
            const max = util.extractNum(this.columnModel.get('max'));
            propVal = util.roundCeilAbsolute(util.extractNum(propVal));

            //Check min-max against reference values
            if (propVal < minValueAccepted) {
                propVal = minValueAccepted;
            } else if (propVal > maxValueAccepted) {
                propVal = maxValueAccepted;
            }

            //Check if min-max are not overflowing each other
            if (propName === 'min' && propVal > max) {
                propVal = max;
            } else if (propName === 'max' && propVal < min) {
                propVal = min;
            }

            //If one of the min-max values is negative then we need to turn on the allowNegative feature
            if (propVal < 0 && !util.isTrue(this.columnModel.get('allowNegative'))){
                this.columnModel.set('allowNegative', true, { silent: true });
            }
        }

        if (isValid) {
            this.columnModel.set(propName, propVal, { silent: true });
        }
        
        numericUtil.transform(this.columnModel);
        this.render();

        undoService.add({
            undo: () => {    
                this.columnModel.set(propName, oldPropVal, { silent: true });
                this.render();
            },
            redo: () => {
                this.columnModel.set(propName, propVal, { silent: true });
                this.render();
            }
        }, textConstants.UNDO_MOVE);
    }

    updateCheckAttributes(ev) {
        ev.preventDefault();
        const propName = ev.currentTarget.name;
        const propVal  = ev.currentTarget.checked;
        const oldPropVal = this.columnModel.get(propName);

        if (propName === 'allowNegative') {
            if (!util.isTrue(propVal)){
                let propText = util.extractNum(this.columnModel.get('text'));
                if (propText < 0){
                    propText = propText * -1;
                    this.columnModel.set('text', propText, { silent: true });
                }
            }
        }
        this.columnModel.set(propName, propVal);
        this.render();

        undoService.add({
            undo: () => {    
                this.columnModel.set(propName, oldPropVal, { silent: true });
                this.render();
            },
            redo: () => {
                this.columnModel.set(propName, propVal, { silent: true });
                this.render();
            }
        }, textConstants.UNDO_MOVE);
    }

    clearAlignmentSelections() {
        ['alignLeftSelection', 'alignCenterSelection', 'alignRightSelection']
            .forEach( v => this.columnModel.set(v, this.unselectedColor, {silent:true} )) ;
    }

    switchAlignment(ev) {
        ev.preventDefault();
        const propName = ev.currentTarget.name;
        const colId = this.colId;
        var newValue = "left";
        const oldValue =  this.model.get('gridData')[colId].attributes['align'];

        this.clearAlignmentSelections();

        if (propName === "align-center") {
            newValue = "center";
            this.model.get('gridData')[colId].attributes['alignCenterSelection'] = this.selectedColor;
        } else if (propName === "align-right") {
            newValue = "right";
            this.model.get('gridData')[colId].attributes['alignRightSelection'] = this.selectedColor;
        }
        else {
            this.model.get('gridData')[colId].attributes['alignLeftSelection'] = this.selectedColor;
        }
        this.model.get('gridData')[colId].attributes['align'] = newValue;
        this.render();

        // this.model.trigger('change');

        // PF-5745
        undoService.add({
            undo: () =>{
                this.model.get('gridData')[colId].attributes['alignLeftSelection'] = this.unselectedColor;
                this.model.get('gridData')[colId].attributes['alignCenterSelection'] = this.unselectedColor;
                this.model.get('gridData')[colId].attributes['alignRightSelection'] = this.unselectedColor;

                switch (oldValue) {
                    case "left": {
                        this.model.get('gridData')[colId].attributes['alignLeftSelection'] = this.selectedColor;
                        break;
                    }
                    case "center": {
                        this.model.get('gridData')[colId].attributes['alignCenterSelection'] = this.selectedColor;
                        break;
                    }
                    case "right": {
                        this.model.get('gridData')[colId].attributes['alignRightSelection'] = this.selectedColor;
                        break;
                    }
                }
                this.model.get('gridData')[colId].attributes['align'] = oldValue;
                this.render();
            },
            redo:() => {
                this.model.get('gridData')[colId].attributes['alignLeftSelection'] = this.unselectedColor;
                this.model.get('gridData')[colId].attributes['alignCenterSelection'] = this.unselectedColor;
                this.model.get('gridData')[colId].attributes['alignRightSelection'] = this.unselectedColor;

                switch (newValue) {
                    case "left": {
                        this.model.get('gridData')[colId].attributes['alignLeftSelection'] = this.selectedColor;
                        break;
                    }
                    case "center": {
                        this.model.get('gridData')[colId].attributes['alignCenterSelection'] = this.selectedColor;
                        break;
                    }
                    case "right": {
                        this.model.get('gridData')[colId].attributes['alignRightSelection'] = this.selectedColor;
                        break;
                    }
                }
                this.model.get('gridData')[colId].attributes['align'] = newValue;
                this.render();
            }
        },textConstants.UNDO_MOVE);
    }


    render() {
        this.$el.empty();
        if(this.template) {
            this.$el.html(this.template(this.model.toJSON()));
        }

        var thousandsSeparator = this.columnModel.get('thousandsSeparator');
        var decimalSeparator = this.columnModel.get('decimalSeparator');

        numericUtil.transform(this.model);

        if (thousandsSeparator) {
            $('#thousandsSeparator').find("option[value='\\" + thousandsSeparator + "']").attr('selected', true);
        }
        if (decimalSeparator) {
            $('#decimalSeparator').find("option[value='\\" + decimalSeparator + "']").attr('selected', true);
        }

        if (this.columnModel.get('paddingAlignment')) {
            var value = this.columnModel.get('paddingAlignment');
            $('#paddingAlignment').val(value);
        }

        let bAllowNegative = util.isTrue(this.model.get('gridData')[this.colId].attributes['allowNegative']);
        $(`input[name='allowNegative']`).prop('checked', bAllowNegative );

        let bShowLeadingZero = util.isTrue(this.model.get('gridData')[this.colId].attributes['showLeadingZero']);
        $(`input[name='showLeadingZero']`).prop('checked', bShowLeadingZero );

        this.delegateEvents();
        return this;
    }

}

export default MainNumericView;
